import { Service, inject } from '@piwikpro/platform';
import { HttpClient } from '@piwikpro/http-crate';
import { Confirmation } from '@piwikpro/confirmation-crate';
import { CustomNotification } from '@piwikpro/notification-crate';
import type { Store } from 'redux';
import {
  PaginateResponse,
  FindOptions,
  CreateResponse,
  DetailsResponse,
  SortDirection,
} from '@piwikpro/json-api';
import { UserGroup, UserGroupAttributes, UserGroupId } from './model';
import {
  userGroupsFetchInitialized,
  userGroupsFetchSucceeded,
  userGroupsFetchFailed,
  userGroupCreationInitialized,
  userGroupCreationSucceeded,
  userGroupCreationFailed,
  userGroupFetchSucceeded,
  userGroupFetchFailed,
  userGroupFetchInitialized,
  userGroupUpdateInitialized,
  userGroupUpdateSucceeded,
  userGroupUpdateFailed,
  userGroupDeleteInitialized,
  userGroupDeleteFailed,
  userGroupDeleteSucceeded,
} from './reduxActions';

import {
  userGroupNonMembersFetchInitialized,
  userGroupNonMembersFetchSucceeded,
  userGroupNonMembersFetchFailed,
  userGroupAddMemberInitialized,
  userGroupAddMemberSucceeded,
  userGroupAddMemberFailed,
  IChangeUserGroupMappingPayload,
  INonMembersPayload,
  IRouterPayload as INonMembersRouterPayload,
  IFilters,
} from './redux/details/nonMembers';

import {
  userGroupMembersFetchInitialized,
  userGroupMembersFetchSucceeded,
  userGroupMembersFetchFailed,
  userGroupMemberDeleteSucceeded,
  IMembersPayload,
  IRouterPayload as IMembersRouterPayload,
} from './redux/details/members';
import { DeleteUserGroupModal } from './components/DeleteUserGroupModal';


@Service()
export class UserGroups {
  constructor(
    @inject('HttpCrate.httpClient') private readonly httpClient: HttpClient,
    @inject('ConfirmationCrate.confirmation') private readonly confirmation: Confirmation,
    @inject('NotificationCrate.interceptors.NotifyOnFail') private readonly notifyOnFail: any,
    @inject('NotificationCrate.interceptors.NotifyAboutDelete') private readonly notifyAboutDelete: any,
    @inject('NotificationCrate.interceptors.NotifyAboutUpdate') private readonly notifyAboutUpdate: any,
    @inject('NotificationCrate.interceptors.NotifyAboutCreation') private readonly notifyAboutCreation: any,
    @inject('NotificationCrate.interceptors.CustomNotification') private readonly customNotification: CustomNotification,
    @inject('config') private readonly config: any,
    @inject('store') private readonly store: Store,
  ) { }

  async find(options: FindOptions = {}): Promise<PaginateResponse<UserGroup> | null> {
    const finalOptions: Required<FindOptions> = Object.assign<
    FindOptions, Required<FindOptions>, FindOptions
    >({}, {
      limit: this.config.get('SIDEBAR_LISTS_ITEMS_PER_PAGE'),
      offset: 0,
      sort: {
        direction: SortDirection.ASC,
        by: 'name',
      },
      search: '',
    }, options);
    const {
      offset,
      search,
      sort,
      limit,
    } = finalOptions;

    this.store.dispatch(userGroupsFetchInitialized(finalOptions));
    try {
      const queryParams = {
        offset,
        limit,
        search,
        sort: sort.direction + sort.by,
      };
      const resp = await this.httpClient.request({
        endpoint: this.config.get('USER_GROUPS_URL'),
        queryParams,
        method: 'GET',
      }).intercept(this.notifyOnFail).jsonApi().send<PaginateResponse<UserGroup>>();
      this.store.dispatch(userGroupsFetchSucceeded(resp));

      return resp;
    } catch (err) {
      this.store.dispatch(userGroupsFetchFailed());

      throw err;
    }
  }

  async findOne(id: UserGroupId): Promise<DetailsResponse<UserGroup> | null> {
    this.store.dispatch(userGroupFetchInitialized());

    try {
      const resp = await this.httpClient.request({
        endpoint: `${this.config.get('USER_GROUPS_URL')}/${id}`,
        method: 'GET',
      }).intercept(this.notifyOnFail).jsonApi().send<DetailsResponse<UserGroup>>();

      this.store.dispatch(userGroupFetchSucceeded(resp.data));

      return resp;
    } catch (err) {
      this.store.dispatch(userGroupFetchFailed());

      throw err;
    }
  }

  async create(attributes: Pick<UserGroupAttributes, 'name'>): Promise<CreateResponse<UserGroup>> {
    this.store.dispatch(userGroupCreationInitialized());
    try {
      const resp = await this.httpClient.request({
        endpoint: this.config.get('USER_GROUPS_URL'),
        method: 'POST',
        body: {
          data: {
            attributes,
            type: 'ppms/user-group',
          },
        },
      }).intercept(this.notifyAboutCreation).jsonApi().send<CreateResponse<UserGroup>>();

      this.store.dispatch(userGroupCreationSucceeded(resp));

      return resp;
    } catch (err) {
      this.store.dispatch(userGroupCreationFailed());

      throw err;
    }
  }

  async update(id: UserGroupId, attributes: Partial<UserGroupAttributes>): Promise<void> {
    this.store.dispatch(userGroupUpdateInitialized(attributes));

    const isRemovingExternalIdpAssignment = attributes.external_id === null;

    const performRequest = async () => {
      try {
        await this.httpClient.request({
          endpoint: `${this.config.get('USER_GROUPS_URL')}/${id}`,
          method: 'PATCH',
          body: {
            data: {
              type: 'ppms/user-group',
              id,
              attributes,
            },
          },
        }).intercept(this.notifyAboutUpdate).jsonApi().send();

        this.store.dispatch(userGroupUpdateSucceeded(attributes));
      } catch (err) {
        this.store.dispatch(userGroupUpdateFailed());

        throw err;
      }
    };

    if (isRemovingExternalIdpAssignment) {
      await this.confirmation.confirm({
        messages: [
          'administration:user-groups.remove-external-idp-assignment-confirm.message-line-1',
          'administration:user-groups.remove-external-idp-assignment-confirm.message-line-2',
        ],
        title: 'administration:user-groups.remove-external-idp-assignment-confirm.title',
        applyBtnText: 'administration:user-groups.remove-external-idp-assignment-confirm.remove-button',
        asyncAction: performRequest,
      });
      return;
    }

    await performRequest();
  }

  async delete(id: UserGroupId, name: string, showExternalIdpInfo = false): Promise<void> {
    await this.confirmation.confirm({
      title: 'administration:user-groups.delete.confirm-title-with-name',
      type: 'customBody',
      name,
      asyncAction: async () => {
        this.store.dispatch(userGroupDeleteInitialized(id));
        try {
          await this.httpClient.request({
            endpoint: `${this.config.get('USER_GROUPS_URL')}/${id}`,
            method: 'DELETE',
          }).intercept(this.notifyAboutDelete).jsonApi().send();

          this.store.dispatch(userGroupDeleteSucceeded(id));
        } catch (err: any) {
          this.store.dispatch(userGroupDeleteFailed());
          throw err;
        }
      },
    }, () => DeleteUserGroupModal({
      showExternalIdpInfo,
    }));
  }

  async fetchUsersOutsideUserGroup(
    id: string,
    filters: IFilters = { search: '' },
  ): Promise<DetailsResponse<INonMembersPayload & INonMembersRouterPayload> | null> {
    this.store.dispatch(userGroupNonMembersFetchInitialized());

    const queryParams = {
      offset: filters.offset,
      limit: filters.limit,
      search: filters.search || '',
    };

    try {
      const resp = await this.httpClient.request({
        endpoint: `${this.config.get('USER_GROUPS_URL')}/${id}/users/excluded`,
        queryParams,
        method: 'GET',
      }).intercept(this.notifyOnFail).jsonApi()
        .send<CreateResponse<INonMembersPayload & INonMembersRouterPayload>>();
      this.store.dispatch(userGroupNonMembersFetchSucceeded(resp));

      return resp;
    } catch (err) {
      this.store.dispatch(userGroupNonMembersFetchFailed());

      throw err;
    }
  }

  async addUsersToUserGroup(id, users): Promise<CreateResponse<IChangeUserGroupMappingPayload>> {
    this.store.dispatch(userGroupAddMemberInitialized());
    try {
      const resp = await this.httpClient.request({
        endpoint: `${this.config.get('USER_GROUPS_URL')}/${id}/relationships/users`,
        method: 'POST',
        body: {
          data: users.map(user => (
            {
              type: 'ppms/user',
              id: user,
            }
          )),
        },
      })
        .intercept(this.notifyAboutCreation)
        .jsonApi()
        .send<CreateResponse<IChangeUserGroupMappingPayload>>();

      this.store.dispatch(userGroupAddMemberSucceeded(resp));

      return resp;
    } catch (err) {
      this.store.dispatch(userGroupAddMemberFailed());

      throw err;
    }
  }

  async fetchUserGroupMembers(id: string,
    filters: IFilters = { search: '' }): Promise<DetailsResponse<IMembersPayload> | null> {
    this.store.dispatch(userGroupMembersFetchInitialized());

    const queryParams = {
      offset: filters.offset,
      limit: filters.limit,
      search: filters.search || '',
    };

    try {
      const resp = await this.httpClient.request({
        endpoint: `${this.config.get('USER_GROUPS_URL')}/${id}/users`,
        queryParams,
        method: 'GET',
      }).intercept(this.notifyOnFail).jsonApi()
        .send<CreateResponse<IMembersPayload & IMembersRouterPayload>>();

      this.store.dispatch(userGroupMembersFetchSucceeded(resp));
      return resp;
    } catch (err) {
      this.store.dispatch(userGroupMembersFetchFailed());

      throw err;
    }
  }

  async deleteMemberFromUserGroup(
    groupId, userId, options?: { toast?: 'default' | 'user-updated' | 'none' },
  ): Promise<CreateResponse<IChangeUserGroupMappingPayload>> {
    try {
      const request = this.httpClient.request({
        endpoint: `${this.config.get('USER_GROUPS_URL')}/${groupId}/relationships/users`,
        method: 'DELETE',
        body: {
          data: [
            {
              type: 'ppms/user',
              id: userId,
            },
          ],
        },
      });

      const toast = options?.toast ?? 'default';

      if (toast === 'user-updated') {
        request.intercept(() => this.customNotification.withConfig({
          pending: {
            text: 'administration:users.groups.notification.pending',
            type: 'pending',
          },
          success: {
            text: 'administration:users.groups.notification.success',
            type: 'success',
          },
          error: {
            text: 'administration:users.groups.notification.error',
            type: 'error',
          },
        }));
      } else if (toast === 'default') {
        request.intercept(this.notifyAboutCreation);
      }

      const resp = await request
        .jsonApi()
        .send<CreateResponse<IChangeUserGroupMappingPayload>>();

      this.store.dispatch(userGroupMemberDeleteSucceeded(resp));

      return resp;
    } catch (err) {
      this.store.dispatch(userGroupAddMemberFailed());

      throw err;
    }
  }
}
